import { ECHTZEIT_ANLASS } from '@mcikm-vote/shared';
import type { EchtzeitAnlass } from '@mcikm-vote/shared';

/**
 * Welche Abfragen ein Anlass ungültig macht (§22, Entscheidung E18).
 *
 * Das Ereignis selbst trägt keine Daten. Diese Zuordnung ist deshalb die gesamte Auswertung: Sie
 * übersetzt „etwas hat sich geändert" in „diese Abfragen sind zu erneuern". Sie steht als reine
 * Funktion getrennt vom Hook, damit sie ohne Netzwerk und ohne React prüfbar ist.
 *
 * Die Schlüssel sind bewusst **Vorsätze**: `['admin', 'abstimmungen']` erfasst auch
 * `['admin', 'abstimmungen', id, 'beteiligung']`. TanStack Query gleicht Schlüssel von vorn ab.
 */

/** Vorsätze der zu erneuernden Abfrageschlüssel. */
export type Abfragevorsatz = readonly (readonly unknown[])[];

const TEILNEHMER_ZUSTAND = ['teilnehmer', 'zustand'] as const;
const ADMIN_ABSTIMMUNGEN = ['admin', 'abstimmungen'] as const;
const ADMIN_VERANSTALTUNGEN = ['admin', 'veranstaltungen'] as const;
const ADMIN_DURCHFUEHRUNG = ['admin', 'durchfuehrung'] as const;

/**
 * Alle Abfragen eines Bereichs.
 *
 * Verwendet nach einer Wiederverbindung: §22.2 verlangt dort eine **vollständige**
 * Zustandsabfrage, weil zwischen Abbruch und Wiederkehr Ereignisse verloren gegangen sein können.
 */
export function alleAbfragen(bereich: 'teilnehmer' | 'admin'): Abfragevorsatz {
  return bereich === 'teilnehmer'
    ? [TEILNEHMER_ZUSTAND]
    : [ADMIN_ABSTIMMUNGEN, ADMIN_VERANSTALTUNGEN, ADMIN_DURCHFUEHRUNG];
}

/**
 * Ein unbekannter Anlass erneuert alles, was der Bereich anzeigt.
 *
 * Eine ältere Oberfläche darf durch einen neuen Anlass nicht stehen bleiben — lieber eine Abfrage
 * zu viel als eine veraltete Anzeige. Der Server bleibt die verbindliche Quelle (§40.3).
 */
export function betroffeneAbfragen(
  anlass: EchtzeitAnlass,
  bereich: 'teilnehmer' | 'admin',
): Abfragevorsatz {
  if (bereich === 'teilnehmer') {
    // Der Teilnehmerbereich hat genau eine Abfrage: seinen Zustand. Sie entscheidet zugleich,
    // welche Ansicht zu zeigen ist (§19.2) — mehr braucht es hier nicht.
    return [TEILNEHMER_ZUSTAND];
  }

  switch (anlass) {
    case ECHTZEIT_ANLASS.BETEILIGUNG:
      // Nur die Zahlen aus §15 — die Abstimmungsliste ändert sich dabei nicht. Die Live-Ansicht
      // gehört dazu: sie zeigt dieselben Zahlen (§20.3).
      return [ADMIN_ABSTIMMUNGEN, ADMIN_DURCHFUEHRUNG];
    case ECHTZEIT_ANLASS.TEILNEHMER:
      return [ADMIN_VERANSTALTUNGEN, ADMIN_ABSTIMMUNGEN, ADMIN_DURCHFUEHRUNG];
    default:
      return [ADMIN_ABSTIMMUNGEN, ADMIN_VERANSTALTUNGEN, ADMIN_DURCHFUEHRUNG];
  }
}
